import {
    collection,
    doc,
    getDocs,
    addDoc,
    updateDoc,
    deleteDoc,
    query,
    orderBy,
    where,
    Timestamp,
    onSnapshot,
    Unsubscribe
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { BusSchedule, CreateScheduleForm } from '../types';
import { RouteService } from './routeService';
import { BusService } from './busService';

export class ScheduleService {
    private static readonly COLLECTION = 'schedules';

    static async getSchedules(startDate?: Date, endDate?: Date): Promise<BusSchedule[]> {
        try {
            const schedulesRef = collection(db, this.COLLECTION);
            let q = query(schedulesRef, orderBy('departureTime', 'asc'));

            if (startDate && endDate) {
                q = query(schedulesRef,
                    where('departureTime', '>=', Timestamp.fromDate(startDate)),
                    where('departureTime', '<=', Timestamp.fromDate(endDate)),
                    orderBy('departureTime', 'asc')
                );
            } else if (startDate) {
                q = query(schedulesRef,
                    where('departureTime', '>=', Timestamp.fromDate(startDate)),
                    orderBy('departureTime', 'asc')
                );
            }

            const snapshot = await getDocs(q);
            return snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    departureTime: data.departureTime?.toDate?.() || new Date(),
                    arrivalTime: data.arrivalTime?.toDate?.() || new Date(),
                } as BusSchedule;
            });
        } catch (error: any) {
            throw new Error(`Failed to fetch schedules: ${error.message}`);
        }
    }

    static subscribeToSchedules(callback: (schedules: BusSchedule[]) => void): Unsubscribe {
        const schedulesRef = collection(db, this.COLLECTION);
        const q = query(schedulesRef, orderBy('departureTime', 'asc'));

        return onSnapshot(q, (snapshot) => {
            const schedules = snapshot.docs.map(doc => {
                const data = doc.data();
                return {
                    id: doc.id,
                    ...data,
                    departureTime: data.departureTime?.toDate?.() || new Date(),
                    arrivalTime: data.arrivalTime?.toDate?.() || new Date(),
                } as BusSchedule;
            });
            callback(schedules);
        }, (error) => {
            console.error('Schedules subscription error:', error);
        });
    }

    static async createSchedule(data: CreateScheduleForm): Promise<string> {
        try {
            // Fetch related data for denormalization
            const [route, bus] = await Promise.all([
                RouteService.getRouteById(data.routeId),
                BusService.getBusById(data.busId)
            ]);

            if (!route) throw new Error('Route not found');
            if (!bus) throw new Error('Bus not found');

            const schedulesRef = collection(db, this.COLLECTION);
            const docRef = await addDoc(schedulesRef, {
                ...data,
                status: 'scheduled',
                departureTime: Timestamp.fromDate(data.departureTime),
                arrivalTime: Timestamp.fromDate(data.arrivalTime),
                availableSeats: bus.totalSeats, // Initialize with total bus capacity
                createdAt: Timestamp.now(),
                updatedAt: Timestamp.now(),

                // Denormalized Route Data
                fromCity: route.fromCity,
                toCity: route.toCity,
                price: route.price,
                duration: route.duration,
                distance: route.distance,

                // Denormalized Bus Data
                busNumber: bus.busNumber,
                operatorName: bus.operatorName,
                busType: bus.busType.name,
                totalSeats: bus.totalSeats,
                amenities: bus.amenities.map(a => a.name)
            });
            return docRef.id;
        } catch (error: any) {
            throw new Error(`Failed to create schedule: ${error.message}`);
        }
    }

    static async updateScheduleStatus(id: string, status: BusSchedule['status']): Promise<void> {
        try {
            const scheduleRef = doc(db, this.COLLECTION, id);
            await updateDoc(scheduleRef, { status });
        } catch (error: any) {
            throw new Error(`Failed to update schedule status: ${error.message}`);
        }
    }

    static async deleteSchedule(id: string): Promise<void> {
        try {
            const scheduleRef = doc(db, this.COLLECTION, id);
            await deleteDoc(scheduleRef);
        } catch (error: any) {
            throw new Error(`Failed to delete schedule: ${error.message}`);
        }
    }
}
